<script setup lang="ts">
type LocationItem = {
  label: string
  plaats_slug: string
  provincie_slug: string
}

const content = {
  title: 'Prestaties van makelaars',
  subtitle: 'Bekijk marktdata en topmakelaars per stad',
  searchLabel: 'Staat je stad er niet bij?',
  searchPlaceholder: 'Zoek je stad of dorp...',
  linkText: 'Bekijk alle plaatsen',
}

const {
  data: locations,
  status,
} = await useAsyncData(
  'popular-cities',
  () => $fetch('/api/data/popular-cities'),
  {
    default: () => [],
  },
)

const searchTerm = ref('')
const selectedCity = ref<LocationItem | undefined>()

const { data: searchResults, execute } = useFetch<LocationItem[]>('/api/fetchLocations', {
  params: { search: searchTerm },
  lazy: true,
})

onMounted(() => {
  watch(searchTerm, () => {
    execute()
  })
})

watch(selectedCity, (city) => {
  if (city) {
    navigateTo(`/makelaars/${city.provincie_slug}/${city.plaats_slug}/`)
  }
})
</script>

<template>
  <section class="py-16">
    <UContainer>
      <div class="text-center mb-10">
        <h2 class="text-3xl sm:text-4xl font-bold tracking-[-0.015em] text-highlighted text-balance mb-2">
          {{ content.title }}
        </h2>
        <p class="text-muted text-lg text-pretty">
          {{ content.subtitle }}
        </p>
      </div>

      <div
        v-if="status === 'pending'"
        class="text-center py-12"
      >
        <UIcon
          name="i-heroicons-arrow-path"
          class="animate-spin size-8 mx-auto text-muted"
        />
      </div>

      <div
        v-else-if="status === 'error'"
        class="text-center py-12 text-muted"
      >
        <p>Er ging iets mis bij het laden van de steden.</p>
      </div>

      <div
        v-else
        class="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        <NuxtLink
          v-for="location in locations"
          :key="location.plaats_slug!"
          :to="`/makelaars/${location.provincie_slug}/${location.plaats_slug}/`"
          class="bg-muted hover:bg-elevated rounded-xl p-4 group transition-colors duration-150"
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-highlighted font-semibold">{{ location.plaats_naam }}</span>
            <UIcon
              name="i-heroicons-chevron-right"
              class="text-dimmed group-hover:text-muted transition-colors duration-150 shrink-0"
            />
          </div>
          <div class="text-xs text-muted leading-relaxed">
            <span>{{ euro(location.city_median_price ?? 0) }}</span>
            <span class="text-dimmed mx-1">·</span>
            <span>{{ formatNumber(location.active_brokers ?? 0) }} mkl.</span>
          </div>
        </NuxtLink>
      </div>

      <div class="mt-10 flex flex-col items-center gap-3">
        <p class="text-sm font-medium text-highlighted">
          {{ content.searchLabel }}
        </p>
        <div class="w-full max-w-sm">
          <UInputMenu
            v-model="selectedCity"
            v-model:search-term="searchTerm"
            icon="i-lucide-search"
            :items="searchResults || []"
            size="xl"
            :placeholder="content.searchPlaceholder"
            :ignore-filter="true"
            trailing-icon=""
            :ui="{
              root: 'w-full',
            }"
          />
        </div>
        <NuxtLink
          to="/makelaars/"
          class="inline-flex items-center text-muted hover:text-highlighted transition-colors duration-150"
        >
          {{ content.linkText }}
          <UIcon
            name="i-heroicons-arrow-right"
            class="ml-2"
          />
        </NuxtLink>
      </div>
    </UContainer>
  </section>
</template>
